import React, { useEffect, useRef, useState } from 'react';
import {
  Film,
  Eye,
  Check,
  CheckSquare,
  XSquare,
  ExternalLink,
  Info,
  GripVertical,
  Plus,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { Button } from '../../ComponnentsUI/common/Button';
import sweep01 from '../../assets/sweeps/sweep1.gif';
import sweep02 from '../../assets/sweeps/sweep2.gif';
import sweep03 from '../../assets/sweeps/sweep3.gif';
import sweep04 from '../../assets/sweeps/sweep4.gif';
import sweep05 from '../../assets/sweeps/sweep5.gif';
import sweep06 from '../../assets/sweeps/sweep6.gif';
import sweep07 from '../../assets/sweeps/sweep7.gif';
import scansImage from '../../assets/scans_00000.png';
interface InstanceData {
  id: number;
  sopInstanceUID: string;
  sopClassUID: string;
  seriesInstanceUID: string;
  instanceNumber: string;
  transferSyntax: string;
  pixelSpacing: string;
  numberOfFrames: number;
  receivedAt: string;
  synced: boolean;
  syncAttemptedAt: string | null;
  syncError: string | null;
  filePath?: string;
  signedUrl?: string;
  signedUrlExpiry?: string;
}

interface InstanceSelectorProps {
  instances: InstanceData[];
  selectedInstances: Set<number>;
  onToggleSelection: (instanceId: number) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onPreviewInstance: (index: number) => void;
  onPreviewAll: () => void;
  examType?: string;
  blindOrder: number[];
  onBlindOrderChange?: (order: number[]) => void;
  selectedSweepBox?: number | null;
  onSweepBoxSelect?: (index: number | null) => void;
  useIndexMode?: boolean;
  onUseIndexModeChange?: (useIndexMode: boolean) => void;
}

export const InstanceSelector: React.FC<InstanceSelectorProps> = ({
  instances,
  selectedInstances,
  onToggleSelection,
  onSelectAll,
  onClearAll,
  onPreviewInstance,
  onPreviewAll,
  examType,
  blindOrder,
  onBlindOrderChange,
  selectedSweepBox,
  onSweepBoxSelect,
  useIndexMode = false,
  onUseIndexModeChange,
}) => {
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [dragOverItem, setDragOverItem] = useState<number | null>(null);
  const [draggedSweep, setDraggedSweep] = useState<number | null>(null);
  const [dragOverInstance, setDragOverInstance] = useState<number | null>(null);

  const sweepMovements = {
    0: { gif: sweep01, name: 'Vertical Center' },
    1: { gif: sweep02, name: 'Vertical Left' },
    2: { gif: sweep03, name: 'Vertical Up' },
    3: { gif: sweep04, name: 'Horizontal R→L' },
    4: { gif: sweep05, name: 'Horizontal L→R' },
    5: { gif: sweep06, name: 'Curved R→L' },
    6: { gif: sweep07, name: 'Curved L→R' },
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const isUrlExpired = (expiryString: string | undefined) => {
    if (!expiryString) return true;
    return new Date(expiryString) < new Date();
  };

  const isBlindStudy = examType === 'Blind';
  const maxBlindInstances = 7;
  const unsyncedInstances = instances.filter((inst) => !inst.synced);
  const unsyncedCount = unsyncedInstances.length;

  const handleBlindSelection = (instanceId: number) => {
    if (!isBlindStudy) {
      onToggleSelection(instanceId);
      return;
    }

    const isCurrentlySelected = selectedInstances.has(instanceId);

    if (isCurrentlySelected) {
      const currentOrder = [...blindOrder];
      const currentIndex = currentOrder.indexOf(instanceId);

      if (currentIndex !== -1) {
        currentOrder[currentIndex] = 0;
        onBlindOrderChange?.(currentOrder);
      }

      onToggleSelection(instanceId);

      onSweepBoxSelect?.(null);
    } else {
      if (selectedSweepBox !== null && selectedSweepBox !== undefined) {
        const currentOrder = [...blindOrder];

        const previousInstanceId = currentOrder[selectedSweepBox];

        const currentIndex = currentOrder.indexOf(instanceId);
        if (currentIndex !== -1) {
          currentOrder[currentIndex] = 0;
        }

        currentOrder[selectedSweepBox] = instanceId;

        const newSelected = new Set(selectedInstances);

        if (previousInstanceId !== 0 && previousInstanceId !== instanceId) {
          newSelected.delete(previousInstanceId);
          onToggleSelection(previousInstanceId);
        }

        newSelected.add(instanceId);

        onBlindOrderChange?.(currentOrder);
        onToggleSelection(instanceId);

        onSweepBoxSelect?.(null);
      } else {
        const currentOrder = [...blindOrder];

        const currentIndex = currentOrder.indexOf(instanceId);
        if (currentIndex !== -1) {
          currentOrder[currentIndex] = 0;
        }

        const nextAvailableIndex = currentOrder.findIndex(
          (sweepId) => sweepId === 0
        );

        if (nextAvailableIndex !== -1) {
          currentOrder[nextAvailableIndex] = instanceId;

          const newSelected = new Set(selectedInstances);
          newSelected.add(instanceId);

          onBlindOrderChange?.(currentOrder);
          onToggleSelection(instanceId);
        }
      }
    }
  };

  const getInstanceSweepNumber = (instanceId: number) => {
    const index = blindOrder.indexOf(instanceId);
    return index !== -1 ? index + 1 : null;
  };

  const handleDragStart = (e: React.DragEvent, instanceId: number) => {
    if (!isBlindStudy) return;
    setDraggedItem(instanceId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', instanceId.toString());
  };

  const handleSweepBoxDragStart = (e: React.DragEvent, sweepIndex: number) => {
    if (!isBlindStudy) return;
    const assignedInstanceId = blindOrder[sweepIndex];
    if (assignedInstanceId === 0) return;

    setDraggedSweep(sweepIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sweepIndex.toString());
  };

  const handleSweepBoxDragOver = (e: React.DragEvent, sweepIndex: number) => {
    if (!isBlindStudy || (!draggedItem && draggedSweep === null)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverItem(sweepIndex);
  };

  const handleSweepBoxDrop = (e: React.DragEvent, sweepIndex: number) => {
    e.preventDefault();
    if (!isBlindStudy) return;

    const currentOrder = [...blindOrder];

    if (draggedItem) {
      const previousInstanceId = currentOrder[sweepIndex];

      const currentIndex = currentOrder.indexOf(draggedItem);
      if (currentIndex !== -1) {
        currentOrder[currentIndex] = 0;
      }

      currentOrder[sweepIndex] = draggedItem;

      const newSelected = new Set(selectedInstances);

      if (previousInstanceId !== 0 && previousInstanceId !== draggedItem) {
        newSelected.delete(previousInstanceId);
        onToggleSelection(previousInstanceId);
      }

      newSelected.add(draggedItem);

      onBlindOrderChange?.(currentOrder);
      onToggleSelection(draggedItem);
    } else if (draggedSweep !== null) {
      const draggedInstanceId = currentOrder[draggedSweep];
      const targetInstanceId = currentOrder[sweepIndex];

      currentOrder[draggedSweep] = targetInstanceId;
      currentOrder[sweepIndex] = draggedInstanceId;

      onBlindOrderChange?.(currentOrder);
    }

    setDraggedItem(null);
    setDraggedSweep(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDraggedSweep(null);
    setDragOverItem(null);
    setDragOverInstance(null);
  };

  const handleSweepBoxClick = (sweepIndex: number) => {
    if (!isBlindStudy || !onSweepBoxSelect) return;
    onSweepBoxSelect(sweepIndex === selectedSweepBox ? null : sweepIndex);
  };

  const handleInstanceDragOver = (
    e: React.DragEvent,
    targetInstanceId: number
  ) => {
    if (!isBlindStudy || !draggedItem || draggedItem === targetInstanceId)
      return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverInstance(targetInstanceId);
  };

  const handleInstanceDrop = (e: React.DragEvent, targetInstanceId: number) => {
    e.preventDefault();
    if (!isBlindStudy || !draggedItem || draggedItem === targetInstanceId)
      return;

    const currentOrder = [...blindOrder];
    const draggedInstanceSweepIndex = currentOrder.indexOf(draggedItem);
    const targetInstanceSweepIndex = currentOrder.indexOf(targetInstanceId);

    if (draggedInstanceSweepIndex !== -1 && targetInstanceSweepIndex !== -1) {
      currentOrder[draggedInstanceSweepIndex] = targetInstanceId;
      currentOrder[targetInstanceSweepIndex] = draggedItem;

      onBlindOrderChange?.(currentOrder);
    }

    setDragOverInstance(null);
  };

  const sortedInstances = isBlindStudy
    ? [...instances].sort((a, b) => {
        const aSweepNumber = getInstanceSweepNumber(a.id);
        const bSweepNumber = getInstanceSweepNumber(b.id);

        if (aSweepNumber !== null && bSweepNumber !== null) {
          return aSweepNumber - bSweepNumber;
        }

        if (aSweepNumber !== null && bSweepNumber === null) return -1;
        if (aSweepNumber === null && bSweepNumber !== null) return 1;

        return 0;
      })
    : instances;
  const sweepPaths = {
    'ultrasound-sweep-1': 'M 50 90 V 65 M 85 65 H 15',
    'ultrasound-sweep-2': 'M 25 35 V 85',
    'ultrasound-sweep-3': 'M 50 35 V 85',
    'ultrasound-sweep-4': 'M 75 35 V 85',
    'ultrasound-sweep-5': 'M 15 50 H 85',
    'ultrasound-sweep-6': 'M 15 75 H 85',
    'ultrasound-sweep-7': 'M 20 80 Q 50 100 80 80',
  };

  // Defines animation properties like duration for each sweep
  const getAnimationDetails = (animationClass) => {
    if (animationClass.includes('sweep-1'))
      return { duration: '3s', timing: 'ease-in-out', alternate: true };
    if (
      animationClass.includes('sweep-2') ||
      animationClass.includes('sweep-3') ||
      animationClass.includes('sweep-4')
    )
      return { duration: '2.5s', timing: 'ease-in-out', alternate: true };
    if (
      animationClass.includes('sweep-5') ||
      animationClass.includes('sweep-6')
    )
      return { duration: '3s', timing: 'ease-in-out', alternate: true };
    if (animationClass.includes('sweep-7'))
      return { duration: '3.5s', timing: 'ease-in-out', alternate: true };
    return { duration: '3s', timing: 'ease-in-out', alternate: true };
  };

  // An array of animation classes to be assigned to each sweep box by index
  const sweepAnimationClasses = [
    'ultrasound-sweep-1',
    'ultrasound-sweep-2',
    'ultrasound-sweep-3',
    'ultrasound-sweep-4',
    'ultrasound-sweep-5',
    'ultrasound-sweep-6',
    'ultrasound-sweep-7',
  ];

  // --- 3. ADDED: The SweepAnimation Component ---

  const SweepAnimation = ({ animationClass }) => {
    const pathRef = useRef(null);
    const [pathLength, setPathLength] = useState(0);

    const pathD = sweepPaths[animationClass] || '';
    const { duration, timing, alternate } = getAnimationDetails(animationClass);
    const dashPattern = '6 6';

    useEffect(() => {
      if (pathRef.current && pathD) {
        setPathLength(pathRef.current.getTotalLength());
      }
    }, [pathD]);

    const pathAnimationName = 'draw-path';

    return (
      <div
        className="h-[100px] w-[100px] rounded-md overflow-hidden relative bg-no-repeat bg-center"
        style={{ backgroundImage: `url(${scansImage})`, backgroundSize: '80%' }}
      >
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            ref={pathRef}
            d={pathD}
            fill="none"
            stroke="rgba(60, 160, 255, 0.7)" // blue color to match your theme
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray={dashPattern}
            style={{
              '--path-length': pathLength,
              strokeDashoffset: pathLength,
              animation:
                pathLength > 0
                  ? `${pathAnimationName} ${duration} ${timing} infinite ${alternate ? 'alternate' : ''}`
                  : 'none',
            }}
          />
        </svg>
        {/* This div is the moving 'probe' marker */}
        <div
          className={`absolute w-2 h-4 bg-blue-500 rounded-sm shadow-md ${animationClass}`}
        />
      </div>
    );
  };
  return (
    // Main container styled to match the image
    <div className="bg-card-color-100 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm w-full mx-auto">
      {/* --- Header Section --- */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-12 h-12 bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 rounded-lg flex items-center justify-center">
            <Film className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-title-page-color-100 dark:text-white">
              Select Study Instances
            </h3>
            <p className="text-sm text-title-page-color-400 dark:text-gray-400 mt-1">
              {isBlindStudy ? (
                <>
                  <span className="font-medium text-title-page-color-300 dark:text-gray-300">
                    {selectedInstances.size}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium text-title-page-color-300 dark:text-gray-300">
                    {maxBlindInstances}
                  </span>{' '}
                  sweeps assigned
                  {selectedInstances.size < maxBlindInstances && (
                    <span className="text-red-500 dark:text-red-400 ml-2">
                      ({maxBlindInstances - selectedInstances.size} more needed)
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="font-medium text-title-page-color-300 dark:text-gray-300">
                    {selectedInstances.size}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium text-title-page-color-300 dark:text-gray-300">
                    {unsyncedCount}
                  </span>{' '}
                  available for sync
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isBlindStudy && (
            <div className="flex items-center gap-2 mr-2 px-3 py-1 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Names
              </span>
              <button
                onClick={() => {
                  onUseIndexModeChange(!useIndexMode);
                }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                {useIndexMode ? (
                  <ToggleRight className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                ) : (
                  <ToggleLeft className="w-5 h-5" />
                )}
              </button>
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Index
              </span>
            </div>
          )}
          {unsyncedCount > 0 &&
            instances.some((instance) => instance.signedUrl) && (
              <Button variant="ghost" onClick={onPreviewAll}>
                <Eye className="w-4 h-4 mr-2" />
                Preview All
              </Button>
            )}
          {!isBlindStudy && (
            <Button
              variant="ghost"
              onClick={onSelectAll}
              disabled={unsyncedCount === 0}
            >
              <CheckSquare className="w-4 h-4 mr-2" />
              Select All
            </Button>
          )}
          <Button variant="secondary" onClick={onClearAll}>
            <XSquare className="w-4 h-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>

      {/* --- Sweep Boxes Section --- */}
      {isBlindStudy && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-3">
            Sweep Assignment (Click a movement then "Select Instance" in viewer,
            or drag instances here)
          </h4>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-5 xl:grid-cols-7 gap-3 place-items-center">
            {Array.from({ length: maxBlindInstances }, (_, index) => {
              const sweepNumber = index + 1;
              const assignedInstanceId = blindOrder[index] || 0;
              const assignedInstance =
                assignedInstanceId !== 0
                  ? instances.find((i) => i.id === assignedInstanceId)
                  : null;
              const isSelected = selectedSweepBox === index;
              const isDragOver = dragOverItem === index;

              return (
                <div
                  key={sweepNumber}
                  draggable={assignedInstance !== null}
                  onDragStart={(e) => handleSweepBoxDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onClick={() => handleSweepBoxClick(index)}
                  onDragOver={(e) => handleSweepBoxDragOver(e, index)}
                  onDrop={(e) => handleSweepBoxDrop(e, index)}
                  onDragLeave={() => setDragOverItem(null)}
                  className={`
        relative p-2 border-2 rounded-lg cursor-pointer transition-all min-h-[90px] flex flex-col items-center justify-center
        ${
          isDragOver
            ? 'border-blue-500 bg-blue-200 dark:bg-blue-800/50 ring-2 ring-blue-400 dark:ring-blue-500'
            : isSelected
              ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/40 ring-2 ring-blue-300 dark:ring-blue-600'
              : assignedInstance
                ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/30 hover:border-blue-400 cursor-grab'
                : 'border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-25 dark:hover:bg-blue-900/20'
        }
      `}
                >
                  {/* <div className="text-xs font-medium text-blue-700 dark:text-blue-300 text-center leading-tight mb-1">
        {useIndexMode ? `Sweep ${sweepNumber}` : sweepMovements[index as keyof typeof sweepMovements]?.name}
      </div> */}

                  {/* --- MODIFIED PART --- */}
                  {assignedInstance ? (
                    <div className="text-center">
                      <div className="text-xs font-medium text-gray-900 dark:text-gray-100">
                        Instance #{assignedInstance.instanceNumber}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {assignedInstance.numberOfFrames} frames
                      </div>
                    </div>
                  ) : (
                    // Conditionally render animation based on useIndexMode
                    <div className="w-full h-full flex-grow">
                      {!useIndexMode ? (
                        <SweepAnimation
                          animationClass={
                            sweepAnimationClasses[
                              index % sweepAnimationClasses.length
                            ]
                          }
                        />
                      ) : (
                        // Show a simple plus icon or sweep number in index mode
                        <div className="flex flex-col items-center justify-center h-full">
                          <div className="text-2xl font-bold text-gray-400 dark:text-gray-500 mb-1">
                            {sweepNumber}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Sweep {sweepNumber}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* --- END OF MODIFICATION --- */}

                  {isSelected && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full"></div>
                  )}
                </div>
              );
            })}
          </div>
          {selectedSweepBox !== null && selectedSweepBox !== undefined && (
            <div className="mt-3 p-2 bg-blue-100 dark:bg-blue-900/40 rounded text-sm text-blue-800 dark:text-blue-300">
              <strong>
                {useIndexMode
                  ? `Sweep ${selectedSweepBox + 1}`
                  : sweepMovements[
                      selectedSweepBox as keyof typeof sweepMovements
                    ]?.name}{' '}
                selected.
              </strong>{' '}
              Now click "Select Instance" in the video viewer to assign an
              instance to this sweep.
            </div>
          )}
        </div>
      )}

      {isBlindStudy && selectedInstances.size !== maxBlindInstances && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            <strong>Blind Study Requirements:</strong> You must assign exactly{' '}
            {maxBlindInstances} instances to sweeps 1-{maxBlindInstances}.
          </p>
        </div>
      )}

      {/* --- Instance Grid --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {sortedInstances.map((instance, index) => {
          const sweepNumber = getInstanceSweepNumber(instance.id);
          const isSelected = selectedInstances.has(instance.id);
          const isDragging = draggedItem === instance.id;
          const isDraggedOver = dragOverInstance === instance.id;

          return (
            <div
              key={instance.id}
              draggable={isBlindStudy && !instance.synced}
              onDragStart={(e) => handleDragStart(e, instance.id)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleInstanceDragOver(e, instance.id)}
              onDrop={(e) => handleInstanceDrop(e, instance.id)}
              onDragLeave={() => setDragOverInstance(null)}
              className={`group relative p-3 border rounded-lg transition-all duration-200 ease-in-out hover:shadow-md ${
                isDragging ? 'opacity-50 scale-95' : ''
              } ${
                isDraggedOver
                  ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/30 ring-2 ring-blue-300 dark:ring-blue-600'
                  : ''
              } ${
                instance.synced
                  ? 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 opacity-60 cursor-not-allowed'
                  : isSelected
                    ? isBlindStudy
                      ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-500 shadow-sm ring-1 ring-blue-200 dark:ring-blue-600'
                      : 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-500 shadow-sm ring-1 ring-blue-200 dark:ring-blue-600 cursor-pointer'
                    : isBlindStudy
                      ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-500 cursor-grab'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500 cursor-pointer'
              }`}
              onClick={() =>
                !instance.synced && handleBlindSelection(instance.id)
              }
            >
              {/* Header Row */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {isBlindStudy && !instance.synced && (
                    <div className="cursor-grab active:cursor-grabbing text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400">
                      <GripVertical className="w-3 h-3" />
                    </div>
                  )}
                  {/* Compact Checkbox */}
                  <div
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      instance.synced
                        ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-600'
                        : isSelected
                          ? isBlindStudy
                            ? 'bg-blue-500 border-blue-500'
                            : 'bg-blue-500 border-blue-500'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 group-hover:border-blue-400 dark:group-hover:border-blue-500'
                    }`}
                  >
                    {instance.synced ? (
                      <Check className="w-2.5 h-2.5 text-green-600 dark:text-green-400" />
                    ) : (
                      isSelected && (
                        <span className="text-white text-xs font-bold">
                          {isBlindStudy ? (
                            sweepNumber
                          ) : (
                            <Check className="w-2.5 h-2.5" />
                          )}
                        </span>
                      )
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                        Instance #{instance.instanceNumber}
                      </h4>
                      {isBlindStudy && isSelected && sweepNumber && (
                        <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded">
                          {useIndexMode
                            ? `S${sweepNumber}`
                            : sweepMovements[
                                (sweepNumber - 1) as keyof typeof sweepMovements
                              ]?.name.slice(0, 3)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      ID: {instance.id}
                    </p>
                  </div>
                </div>

                {/* Status Badge */}
                <div
                  className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                    instance.synced
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                  }`}
                >
                  {instance.synced ? 'Synced' : 'Pending'}
                </div>
              </div>

              {/* Content Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <Film className="w-3 h-3" />
                    {instance.numberOfFrames} frames
                  </span>
                  <span className="text-gray-400 dark:text-gray-500">•</span>
                  <span>{formatDate(instance.receivedAt)}</span>
                  {isBlindStudy &&
                    isSelected &&
                    sweepNumber &&
                    !useIndexMode && (
                      <>
                        <span className="text-gray-400 dark:text-gray-500">
                          •
                        </span>
                        <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400 font-medium">
                          {
                            sweepMovements[
                              (sweepNumber - 1) as keyof typeof sweepMovements
                            ]?.name
                          }
                        </span>
                      </>
                    )}
                </div>

                {/* Action Buttons */}
                {!instance.synced && instance.signedUrl && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreviewInstance(index);
                      }}
                      className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors"
                      title="Preview Video"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isUrlExpired(instance.signedUrlExpiry))
                          window.open(instance.signedUrl, '_blank');
                      }}
                      className="p-1 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Open in New Tab"
                      disabled={isUrlExpired(instance.signedUrlExpiry)}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <style>{`
    /* Animation for the SVG path drawing itself */
    @keyframes draw-path {
      from { stroke-dashoffset: var(--path-length); }
      to { stroke-dashoffset: 0; }
    }

    /* Animations for the moving 'probe' marker */
    @keyframes sweep-1 {
      0% { top: 90%; left: 40%; transform: rotate(90deg) translate(-50%, -50%); opacity: 1; }
      25% { top: 65%; left: 40%; transform: rotate(90deg) translate(-50%, -50%); opacity: 1; }
      35% { top: 65%; left: 85%; transform: rotate(0deg) translate(-50%, -50%); opacity: 1; }
      100% { top: 65%; left: 15%; transform: rotate(0deg) translate(-50%, -50%); opacity: 1; }
    }
    .ultrasound-sweep-1 { 
      animation: sweep-1 3s ease-in-out infinite alternate; 
    }

    @keyframes sweep-2 { 0% { top: 35%; transform: rotate(90deg) } 100% { top: 85%; transform: rotate(90deg)} }
    .ultrasound-sweep-2 { left: 21%; transform: translateX(-50%); animation: sweep-2 2.5s ease-in-out infinite alternate; }

    @keyframes sweep-3 { 0% { top: 35%;  transform: rotate(90deg)} 100% { top: 85%;  transform: rotate(90deg)} }
    .ultrasound-sweep-3 { left: 46%; transform: translateX(-50%); animation: sweep-3 2.5s ease-in-out infinite alternate; }

    @keyframes sweep-4 { 0% { top: 35%; transform: rotate(90deg)} 100% { top: 85%; transform: rotate(90deg)} }
    .ultrasound-sweep-4 { left: 71%; transform: translateX(-50%); animation: sweep-4 2.5s ease-in-out infinite alternate; }

    @keyframes sweep-5 { 0% { left: 15%; } 100% { left: 85%; } }
    .ultrasound-sweep-5 { top: 50%; transform: translate(-50%, -50%); animation: sweep-5 3s ease-in-out infinite alternate; }

    @keyframes sweep-6 { 0% { left: 15%; } 100% { left: 85%; } }
    .ultrasound-sweep-6 { top: 75%; transform: translate(-50%, -50%); animation: sweep-6 3s ease-in-out infinite alternate; }

    @keyframes sweep-7 { 0% { top: 80%; left: 20%; } 50% { top: 90%; left: 50%; } 100% { top: 80%; left: 80%; } }
    .ultrasound-sweep-7 { animation: sweep-7 3.5s ease-in-out infinite alternate; transform: translate(-50%, -50%); }
`}</style>
    </div>
  );
};
